import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';
import type { Post, PostPage } from '@/lib/schemas/post';

import { feedApi, type CreatePostInput } from './api';
import { filtersToApiQuery, type FeedFilters } from './filters';

export function useFeed(
  filters: FeedFilters,
): UseInfiniteQueryResult<{ pages: PostPage[]; pageParams: unknown[] }, Error> {
  const query = filtersToApiQuery(filters);

  return useInfiniteQuery({
    queryKey: queryKeys.feed(query),
    queryFn: ({ pageParam, signal }) =>
      feedApi.list(
        typeof pageParam === 'string' && pageParam ? { ...query, cursor: pageParam } : query,
        signal,
      ),
    initialPageParam: '',
    // The API tells us where the next page starts; null means we reached the end.
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

export function usePost(id: string): UseQueryResult<Post, Error> {
  return useQuery({
    queryKey: queryKeys.post(id),
    queryFn: ({ signal }) => feedApi.get(id, signal),
  });
}

export function usePostIdeals(id: string, enabled: boolean): UseQueryResult<Post[], Error> {
  return useQuery({
    queryKey: queryKeys.postIdeals(id),
    queryFn: ({ signal }) => feedApi.ideals(id, signal),
    enabled,
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePostInput) => feedApi.create(input),
    onSuccess: (post) => {
      // Every feed variant may now be stale — a new post can match any filter
      // combination, so invalidating the whole feed root is the honest move.
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedRoot });
      queryClient.setQueryData(queryKeys.post(post.id), post);
      if (post.linkedPost) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.postIdeals(post.linkedPost.id) });
      }
    },
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; body?: string; tags?: string[] }) =>
      feedApi.update(id, body),
    onSuccess: (post) => {
      // The server rewrites mention labels on save, so the returned post is the
      // truth — seeding it means the card shows the canonical text immediately
      // rather than briefly showing what was typed.
      queryClient.setQueryData(queryKeys.post(post.id), post);
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedRoot });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profileRoot });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => feedApi.remove(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.post(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedRoot });
    },
  });
}
